import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import agent, { mocked } from 'agent'

import appMode from 'helpers/getAppMode'
import { connectAppNotifications, disconnectAppNotifications } from 'redux/actions/app.actions'
import { exportSaga } from './export.sagas'

import * as actions from 'redux/actions/user.actions'
import NotificationToast from '_base/Notification'

function* registerSaga(params) {
  const { email, password, firstName, lastName } = params.payload

  try {
    const body = yield call(agent.Auth.register, email, password, firstName, lastName)

    // Set JWT token and take user to domain wizard
    const token = body.data.attributes.jwt
    const data = body.data.attributes.user
    localStorage.setItem('jwt', token)

    yield call(agent.setToken, token)

    yield put(actions.registerSuccess(token, data))

    yield put(push('/domains/add'))
  } catch (error) {
    const errors = error.response.body.errors

    yield put(actions.registerFailure(errors))
  }
}

function* loginSaga(params) {
  const { email, password } = params.payload
  try {
    const body = yield call(agent.Auth.login, email, password)

    // Set JWT token and take user to domain wizard
    const token = body.data.attributes.jwt
    const data = body.data.attributes.user

    localStorage.setItem('jwt', token)

    yield call(agent.setToken, token)

    yield put(actions.loginSuccess(token, data))
    yield put(actions.getCurrentUser())
    yield put(push('/dashboard'))
  } catch (error) {
    const errors = error.response.body.errors
    yield put(actions.loginFailure(errors))
  }
}

function* autoLoginSaga(params) {
  try {
    const { token } = params.payload
    localStorage.setItem('jwt', token)

    yield call(agent.setToken, token)

    // Get domains so we know where to redirect
    const body = yield call(agent.Domains.all)
    const domains = body.data

    yield put(actions.autoLoginSuccess(token))

    if (domains.length > 0) {
      yield put(push('/dashboard?redirect=auto'))
    } else {
      yield put(push('/domains/add?redirect=auto'))
    }
  } catch (error) {
    const errors = error.response.body.errors

    yield put(actions.autoLoginFailure(errors))
  }
}

function* logoutSaga() {
  try {
    localStorage.removeItem('jwt')

    yield put(disconnectAppNotifications())

    yield put(actions.logoutSuccess())

    yield put(push('/'))
    if (window) window.location.reload()
  } catch (error) {
    yield put(actions.logoutFailure(error))
  }
}

function* updateProfileSaga(params) {
  try {
    const { id, payload } = params.payload
    const { user } = payload

    const state = yield select()
    yield call(agent.setToken, state.user.token)

    yield call(agent.Auth.update, id, payload)

    const successData = {
      data: {
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email
      }
    }
    NotificationToast('success', 'Profile successfully updated')
    yield put(actions.updateProfileSuccess(successData))
  } catch (error) {
    let errors = []
    if (error.response.status === 401) {
      errors.push(401)
      NotificationToast('error', 'The current password you entered is incorrect')
    } else {
      NotificationToast('error', 'There was a problem updating your profile')
    }

    yield put(actions.updateProfileFailure(errors))
  }
}

function* updatePasswordSaga(params) {
  const { id, payload } = params.payload

  try {
    const state = yield select()
    yield call(agent.setToken, state.user.token)

    yield call(agent.Auth.update, id, payload)
    NotificationToast('success', 'Your password was successfully updated')

    yield put(actions.updatePasswordSuccess())
  } catch (error) {
    if (error.response.status === 401) {
      NotificationToast('error', 'The current password you entered is incorrect')
    }
    yield put(actions.updatePasswordFailure(error))
  }
}

function* getCurrentUserSaga() {
  try {
    const state = yield select()
    let endpoint = agent.Auth.current
    if (appMode.preview) {
      endpoint = mocked.Auth.current
    } else {
      yield call(agent.setToken, state.user.token)
      yield put(connectAppNotifications())
    }

    const body = yield call(endpoint)
    const user = body.data.attributes
    const id = body.data.id

    yield put(actions.getCurrentUserSuccess(user, id))
  } catch (error) {
    yield put(actions.getCurrentUserFailure(error))
    yield call(logoutSaga)
  }
}

function* exportPersonalData() {
  yield call(exportSaga, {
    params: {},
    type: 'gdpr',
    request: agent.Auth.gdpr
  })
}

function* getUserEmailNotificationsSaga() {
  try {
    const state = yield select()
    yield call(agent.setToken, state.user.token)
    const body = yield call(agent.Users.getEmailNotifications)

    yield put(actions.getEmailNotificationsSuccess(body.data))
  } catch (error) {
    yield put(actions.getEmailNotificationsFailure(error))
  }
}

function* setUserEmailNotificationsSaga(params) {
  const { id, payload } = params.payload

  try {
    const state = yield select()
    yield call(agent.setToken, state.user.token)
    yield call(agent.Users.setEmailNotifications, id, payload)

    // Refresh the users
    yield put(actions.getEmailNotifications())

    yield put(actions.setEmailNotificationsSuccess())
  } catch (error) {
    yield put(actions.setEmailNotificationsFailure(error))
  }
}

export default function* accountSagas() {
  yield all([
    takeLatest(actions.getCurrentUser.type, getCurrentUserSaga),
    takeLatest(actions.getPersonalData.type, exportPersonalData),
    takeLatest(actions.register.type, registerSaga),
    takeLatest(actions.login.type, loginSaga),
    takeLatest(actions.autoLogin.type, autoLoginSaga),
    takeLatest(actions.logout.type, logoutSaga),
    takeLatest(actions.updateProfile.type, updateProfileSaga),
    takeLatest(actions.updatePassword.type, updatePasswordSaga),
    takeLatest(actions.getEmailNotifications.type, getUserEmailNotificationsSaga),
    takeLatest(actions.setEmailNotifications.type, setUserEmailNotificationsSaga)
  ])
}
